import * as _ from "lodash";
import { ISQLGroupByCalc } from "../../server-pa/PA.Core/PA.Core.IF";
import { PAWebSocketClient } from "./PAWebSocketClient";

export interface ISearchRequest {
    model?: string; // table_name or view_name w/o /AZR/
    where?: any;
    whereString?: string;
    fields?: { [field_name_id: string]: { type: string } };
    limit?: number;
    order?: string[]; // name ASC
    distinct?: string[]; // field ids
    groupByCalcs?: ISQLGroupByCalc[];
    bCountOnly?: boolean;
    sqlQuery?: string;
}

export class PASearch {
    private static wsc: PAWebSocketClient;

    static find(
        pViewName: string,
        pWhereConditionJSON: { [field_id: string]: any },
        pWhereFieldSpecJSON: { [field_id: string]: { type: string; [id: string]: any } },
        pOrderByFieldNames: string[],
        pLimitRowCount: number,
        pDistinctFieldsNames: string[],
        pGroupByCalcs?: ISQLGroupByCalc[],
    ): Promise<any[]> {
        return new Promise<any[]>((resolve, error) => {
            // Create a Request Object
            // see below for example request spec
            //

            const sViewName: string = pViewName || "dummy";
            const whereConditionJSON: any = _.cloneDeep(pWhereConditionJSON || {});

            // This next step prevents the whole search profile spec from being sent to the server.
            const whereFieldSpecJSON: any = pWhereFieldSpecJSON || {};
            const orderByFieldNames: string[] = _.clone(pOrderByFieldNames || []);
            const limitRowCount: number = pLimitRowCount || 0;
            const distinctFieldsNames: string[] = _.clone(pDistinctFieldsNames || []);
            const groupByCalcs: ISQLGroupByCalc[] = _.clone(pGroupByCalcs || []);
            const requestSpec: ISearchRequest = {
                model: sViewName,
            };

            //The whereCondition requires a FieldSpec so we can determine the SQL formatting on the server..
            //  any field in the where condition... needs a field spec attribute..
            // { or: [{ WB_CUST_ID:    1},..
            // would require a field spec for WB_CUST_ID
            // {
            //    WB_CUST_ID:     {type: 'number'},
            //    ...

            if (!_.isEmpty(whereConditionJSON)) {
                if ("where" in whereConditionJSON) requestSpec.where = whereConditionJSON.where;
                else requestSpec.where = whereConditionJSON;

                if ("fields" in whereFieldSpecJSON)
                    requestSpec.fields = PASearch.convertFieldTypes(whereFieldSpecJSON.fields);
                else requestSpec.fields = PASearch.convertFieldTypes(whereFieldSpecJSON);
            }

            if (limitRowCount > 0) requestSpec.limit = limitRowCount;

            if (orderByFieldNames.length > 0) requestSpec.order = orderByFieldNames;

            if (distinctFieldsNames.length > 0) requestSpec.distinct = distinctFieldsNames;

            if (groupByCalcs.length > 0) requestSpec.groupByCalcs = groupByCalcs;

            this.findByRequest(requestSpec)
                .then((results: any[]) => {
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static findByRequest(aRequest: ISearchRequest): Promise<any[]> {
        return new Promise<any[]>(async (resolve, reject) => {
            if ("fields" in aRequest) {
                aRequest.fields = PASearch.convertFieldTypes(aRequest.fields);
            }

            if ("where" in aRequest) {
                aRequest.where = _.cloneDeep(aRequest.where);
            }

            let results: any;

            try {
                PASearch.wsc = new PAWebSocketClient();
                results = await PASearch.wsc.invoke("PASearchService", aRequest, { ackTimeout: 60 * 1000 * 30 });
                if (_.isArray(results)) {
                    resolve(results);
                } else {
                    reject("Unable to parse Search Web Response: " + results);
                }
            } catch (err) {
                reject("Unable to obtain Search Results from Server. " + err);
            }
        });
    }

    static createSimpleSchema(stringFields: string[], numberFields: string[]): { [id: string]: { type: string } } {
        const rv: { [id: string]: { type: string } } = {};
        _.forEach(stringFields, (a) => {
            rv[a] = { type: "string" };
        });
        _.forEach(numberFields, (a) => {
            rv[a] = { type: "number" };
        });
        return rv;
    }

    // Map ISearch types to db types..
    //  also, remove extra cruft from ISearch field specs..
    private static convertFieldTypes(inSpec: { [field_id: string]: { type: string; [id: string]: any } }): {
        [field_id: string]: { type: string };
    } {
        return _.mapValues(inSpec || {}, (value, key) => {
            let newType = value.type;

            switch (value.type) {
                case "sapdate":
                case "boolean":
                    newType = "string";
                    break;
                case "int":
                case "float":
                case "currency":
                    newType = "number";
                    break;
            }
            return { type: newType };
        });
    }
}
